import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { RowNode } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { isNull, isUndefined, isArray } from '../../models/utils';
import { ContextMenuOption } from '../../models/context-menu-option';
import { DrRequestInfo } from '../../models/dr-request-info';
import { Endpoint, MessageType, SearchDialect, SettingsType } from '../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, GenericColDef } from '../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions, ObjectActionSet } from '../../models/permission';
import { Project } from '../../models/project';
import { ProjectListItem } from '../../models/project-list-item';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { CreateEditEntityModalComponent } from '../modals/create-edit-entity-modal/create-edit-entity-modal.component';
import { DeleteEntityModalComponent } from '../modals/delete-entity-modal/delete-entity-modal.component';
import { ExportProjectModalComponent } from '../modals/export-project-modal/export-project-modal.component';
import { ImportProjectModalComponent } from '../modals/import-project-modal/import-project-modal.component';
import { NewProjectModalComponent } from '../modals/new-project-modal/new-project-modal.component';
import { SettingsModalComponent } from '../modals/settings-modal/settings-modal.component';
import { DrEntityAttribute } from '../../models/dr-entity-attribute';

@Component({
    standalone: false,
    selector: 'res-project-list',
    templateUrl: './project-list.component.html',
    styleUrls: ['./project-list.component.scss']
})

export class ProjectListComponent implements OnInit, OnDestroy {
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @Input() filterQuery: string;

    readonly DefaultClauseCount = 1000;
    debounceFilter = _.debounce(this.filterAfterBounce, 1000);

    isSuperSystemUser: boolean;
    activeFilter = '';
    private entityChangedEvent: Subscription;
    isRefresh: boolean;
    isExpressSystem: boolean | undefined = undefined;

    gridName = 'Projects';
    automationId = 'project-list';

    isBusy = false;
    isBusyMessage: string;
    username: string;
    systemRolePermissions: Array<ObjectActionSet>;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Organization',
            field: 'organizationName',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.organizationName,
            lockVisible: true
        },
        {
            headerName: 'Organization Description',
            field: 'organizationDescription',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.organizationDesc,
            lockVisible: true
        },
        {
            headerName: 'Project',
            field: 'name',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.linkIconLabel,
            reviewIcon: true,
            claimsIcon: true,
            lockVisible: true
        },
        {
            headerName: 'Project ID',
            field: 'handle',
            automationId: this.automationId,
            width: 100,
            suppressSizeToFit: true,
            lockVisible: true
        },
        {
            headerName: 'Review Project',
            field: 'reviewProjects',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.reviewProjectsWithTooltip,
            width: 250,
            suppressSizeToFit: true,
            lockVisible: true
        },
        {
            headerName: 'Project Description',
            field: 'description',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Date Created',
            field: 'dateCreated',
            automationId: this.automationId,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            width: 147,
            suppressSizeToFit: true,
            lockVisible: true
        },
        {
            headerName: 'Status',
            field: 'projectStateDisplay',
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            automationId: this.automationId,
            width: 105,
            suppressSizeToFit: true,
            lockVisible: true
        },
        {
            headerName: 'Parsing Version',
            field: 'parsingVersion',
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            automationId: this.automationId,
            width: 120,
            suppressSizeToFit: true,
            lockVisible: true
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true,
            suppressMovable: true
        }
    );
    requestInfo = new DrRequestInfo({});
    contextMenuOptions = [
        new ContextMenuOption({
            name: 'New Project ...',
            click: (item: ProjectListItem) => this.projectListContextMenuActions('newProject', item),
            visible: (item: ProjectListItem) => item && item.isParent && (this.isSuperSystemUser ?
                item.hasProjectCreatePermissions : Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.CREATE)),
            iconClass: 'fa-plus',
            iconColor: 'color-green-04'
        }),
        new ContextMenuOption({
            name: 'Import Project ...',
            click: (item: ProjectListItem) => this.projectListContextMenuActions('importProject', item),
            visible: (item: ProjectListItem) => item && item.isParent && (this.isSuperSystemUser ?
                item.hasProjectCreatePermissions : Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.CREATE)),
            iconClass: 'fa-reply fa-rotate-180',
            iconColor: 'color-green-04'
        }),
        new ContextMenuOption({
            divider: true,
            visible: (item: ProjectListItem) => item && item.isParent
        }),
        new ContextMenuOption({
            name: 'Export ...',
            click: (item: ProjectListItem) => this.projectListContextMenuActions('exportProject', item),
            enabled: (item: ProjectListItem) => !item.isDisabled('Export ...'),
            visible: (item: ProjectListItem) => item && item.isChild && (this.isSuperSystemUser ?
                item.parent.hasProjectCreatePermissions : Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.CREATE)),
            iconClass: 'fa-share',
            iconColor: 'color-purple-01'
        }),
        new ContextMenuOption({
            name: 'Settings ...',
            click: (item: ProjectListItem) => this.projectListContextMenuActions('openSettings', item),
            visible: (item: ProjectListItem) => item && item.isParent &&
                (this.isSuperSystemUser ? item.hasSysSettingsPermissions : Permissions.hasOrgSettingsPermissions),
            iconClass: 'fa-cogs',
            iconColor: 'color-yellow-07',
        }),
        new ContextMenuOption({
            name: 'Settings ...',
            click: (item: ProjectListItem) => this.projectListContextMenuActions('openSettings', item),
            enabled: (item: ProjectListItem) => !item.isDisabled('Settings ...'),
            visible: (item: ProjectListItem) => item && item.isChild &&
                (this.isSuperSystemUser ? item.parent.hasPrjSettingsPermissions : Permissions.hasPrjSettingsPermissions),
            iconClass: 'fa-cogs',
            iconColor: 'color-blue-01',
        }),
        new ContextMenuOption({
            name: 'Edit ...',
            click: (item: ProjectListItem) => this.projectListContextMenuActions('editProject', item),
            enabled: (item: ProjectListItem) => !item.isDisabled('Edit ...'),
            visible: (item: ProjectListItem) => item && item.isChild && (this.isSuperSystemUser ?
                item.parent.hasProjectCreatePermissions : Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.CREATE)),
            iconClass: 'fa-pencil',
            iconColor: 'color-blue-01'
        }),
        new ContextMenuOption({
            name: 'Delete',
            click: (item: ProjectListItem) => this.projectListContextMenuActions('deleteProject', item),
            enabled: (item: ProjectListItem) => !item.isDisabled('Delete'),
            visible: (item: ProjectListItem) => item && item.isChild && (this.isSuperSystemUser ?
                item.parent.hasProjectDeletePermissions : Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.DELETE)),
            iconClass: 'fa-trash',
            iconColor: 'color-red-05'
        })
    ];

    rowClassRules = {
        'dark-row': (params: any): any => {
            return (params.data && params.data.isParent);
        },
        'disabled-row': (params: any): boolean => {
            return false;
        },
        'disabled-cells': (params: any): boolean => {
            return ((params.data instanceof ProjectListItem) && (params.data as ProjectListItem).isDisabled());
        }
    };

    responseCallback = (response: any): void => {
        if (response) {
            const user = this.commonService.currentUser;
            this.username = user.userName;
            if (!(this.requestInfo.data.filters?.length > 0)) {
                this.commonService.currentUser.projects = _.flatten(_.map(response.userOrgProjects, org => org.projects), 1);
                this.commonService.sendMessage(MessageType.updateSystemSettingsProjectList);
            }
            if (this.activeFilter && this.activeFilter.length > 0 && this.requestInfo.data.filters && this.requestInfo.data.filters[0].value !== this.activeFilter) {
                response.userOrgProjects = [];
                return;
            }
            const listedItems = this.genericGrid.getRows();
            response.userOrgProjects = this.mapOrganizations(response.userOrgProjects);
            _.each(response.userOrgProjects, (org: ProjectListItem) => {
                if (org.description) {
                    org.organizationDescription = org.description;
                    delete org.description;
                }
                const existingOrg = _.filter(listedItems, (item) => item.data.organizationName === org.organizationName);
                const map = org.role.objectActionList ? Permissions.mapFromObjectActionList(org.role.objectActionList) : undefined;
                org.hasOrgSettingsPermissions = Permissions.checkOrgPrjSettingsPermissions('ORG_', map);
                org.hasPrjSettingsPermissions = Permissions.checkOrgPrjSettingsPermissions('PRJ_', map);
                if (this.isSuperSystemUser) {
                    org.hasSysSettingsPermissions = Permissions.checkOrgPrjSettingsPermissions('SYSTEM_', map);
                } else {
                    org.isExpanded = response.userOrgProjects?.length === 1;
                }
                org.hasProjectCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.CREATE, map);
                org.hasProjectDeletePermissions = Permissions.checkUserPermission(ObjectActionListType.PROJECT, ActionType.DELETE, map);
                if (existingOrg && existingOrg.length > 0) {
                    if (this.activeFilter || (this.isRefresh && existingOrg[0].data.isExpanded)) {
                        org.isExpanded = true;
                    }
                }
            });

            setTimeout(() => {
                this.isRefresh = false;
            }, 500);
        }
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType): void => {
        if (rowNode && rowNode.data instanceof ProjectListItem && action === CellActionType.linkLabelClick && rowNode.data.isChild && rowNode.data.handle) {
            this.selectProject(rowNode.data);
        }
    }
    onCellClicked = (event): void => {
        if (event && event.data instanceof ProjectListItem && event.data.isParent && event.node && (!event.colDef || (<any>event.colDef).cellTemplateType !== CellTemplateType.contextMenu)) {
            this.genericGrid.expandCollapseRow(event.node);
        }
    }
    cellDoubleClickedCallback = (rowNode: RowNode): void => {
        if (rowNode && rowNode.data instanceof ProjectListItem && rowNode.data.isChild && !rowNode.data.isDisabled()) {
            this.selectProject(rowNode.data);
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private router: Router) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.project).subscribe((data) => {
            if (data && isArray(data)) {
                if (data[1] && data[1].project) {
                    this.commonService.currentProject = data[1].project;
                    this.commonService.openInformationToastNotification('Project import Started');
                    this.loadData();
                }
            } else {
                this.initializeRolesAndPermissions(true);
            }
        });
        this.commonService.currentProject = null;
    }

    ngOnInit(): void {
        this.commonService.mainContent = null;
        if (isNull(this.commonService.realmADFSEnabled) || isUndefined(this.commonService.realmADFSEnabled) || isUndefined(this.isExpressSystem)) {

            this.restService.post(Endpoint.realmManager_getRealm).then(response => {
                if (response) {
                    this.commonService.realmADFSEnabled = response.adfsEnabled;
                    this.isExpressSystem = this.commonService.tlsExpressSystem = response.tlsExpress || false;
                    this.commonService.reefReviewEnabled = response.reefReviewEnabled;
                    this.commonService.isTPAuthEnabledSystem = response.tpAuth;
                    this.commonService.maxBooleanClauseCount = response.maxBooleanClauseCount;
                    this.commonService.maxBooleanClauseCount = response.maxBooleanClauseCount ? Math.round(response.maxBooleanClauseCount / 1000) * 1000 : this.DefaultClauseCount;
                }
            });
        }
        this.initializeRolesAndPermissions();
    }

    initializeRolesAndPermissions(refresh = false): void {
        if (this.commonService.currentUser.customerName !== this.commonService.currentUser.originalCustomerName) {
            this.commonService.currentUser.customerName = this.commonService.currentUser.originalCustomerName;
        }

        this.isSuperSystemUser = (this.commonService && this.commonService.currentUser) ?
            this.commonService.currentUser.customerName === 'super_system_customer' : false;

        if (!this.commonService.isUserAdfsAuthenticated) {
            this.getUserPermissions();
        } else {
            // is case of system user login, without setTimeout the grid is not fully initialized and tries to loadObservableData with null requestInfo
            setTimeout(() => {
                this.loadData(refresh);
            });
        }
    }

    getUserPermissions(): void {
        if (this.commonService.currentUser) {
            let handle = '';
            if (this.commonService.currentProject) {
                handle = this.commonService.currentProject.handle;
            } else if (this.commonService.isUserAdfsAuthenticated || this.commonService?.currentUser?.customerName) {
                handle = this.commonService?.currentUser?.customerName;
            } else if (this.commonService.currentOrganizationHandle) {
                handle = this.commonService.currentOrganizationHandle;
            }

            if (handle) {
                this.isBusyMessage = 'Loading project permissions ...';
                this.isBusy = true;
                this.restService.post(Endpoint.permissionManager_getCombinedUserRole, { entityHandle: handle }).then(roleResponse => {
                    let roles = roleResponse.role.objectActionList;
                    if (this.isSuperSystemUser || this.commonService.currentUser.importedFromSystemUser) {
                        this.restService.post(Endpoint.permissionManager_getCombinedUserRole, { entityHandle: 'super_system_customer' }).then(systemRoleResponse => {
                            this.isBusy = false;
                            this.systemRolePermissions = systemRoleResponse.role.objectActionList;
                            roles = [...roles, ...systemRoleResponse.role.objectActionList];
                            this.setPermissionAndNavigate(roles);
                        }, () => {
                            this.isBusy = false;
                        });
                    } else {
                        this.isBusy = false;
                        this.setPermissionAndNavigate(roles);
                    }
                }, () => {
                    this.isBusy = false;
                });
            }
        }
    }

    setPermissionAndNavigate(roleResponse): void {
        if (roleResponse) {
            Permissions.setPermissions(roleResponse);
            this.commonService.sendMessage(MessageType.updatePermissions);
        }
        if (this.commonService.currentProject) {
            this.initializeOrNavigateToPage('main-content');
        } else {
            this.initializeOrNavigateToPage('login');
        }
    }

    initializeOrNavigateToPage(page: any): void {
        const message = !this.commonService.currentProject ? 'You do not have permissions to open a Project!' : 'Not Authorized for Administration on ' + this.commonService.currentProject.name + '!';
        if (this.isSuperSystemUser && !Permissions.checkUserPermission(ObjectActionListType.PROJECTS)) {
            this.commonService.openWarningModal(message);
            this.isBusy = false;
            if (!this.commonService.currentProject) {
                this.router.navigate([page], { skipLocationChange: true });
            }
        } else if (!this.isSuperSystemUser && !Permissions.checkUserPermission(ObjectActionListType.PROJECT)) {
            this.commonService.openWarningModal(message);
            this.isBusy = false;
            if (!this.commonService.currentProject) {
                this.router.navigate([page], { skipLocationChange: true });
            }
        } else {
            this.isBusy = false;
            if (!this.commonService.currentProject) {
                this.loadData(true);
            } else {
                this.isBusy = true;
                this.isBusyMessage = 'Loading Project ...';
                this.restService.post(Endpoint.projectManager_getProject, { handle: this.commonService.currentProject.handle }).then((response) => {
                    this.isBusy = false;
                    if (response?.project?.attributes) {
                        this.setTlsProjectInfoFromAttributes(response.project.attributes);
                    }
                    this.loadProjectSettings();
                    this.router.navigate([page], { skipLocationChange: true });
                }, () => {
                    this.isBusy = false;
                });
            }
        }
    }

    setTlsProjectInfoFromAttributes(attributes: Array<DrEntityAttribute>): void {
        if (attributes?.length > 0) {
            const expStreamAttribute = attributes.find(a => a.name === 'TLS_EXPRESS_DEFAULT_EXPORT_STREAM' || a.name === 'REEF_EXPRESS_DEFAULT_EXPORT_STREAM');
            if (expStreamAttribute) {
                this.commonService.currentProject.tlsDefaultExportStreamHandle = expStreamAttribute.value;
            }
            const tagAttribute = attributes.find(a => a.name === 'TLS_EXPRESS_DEFAULT_TAG' || a.name === 'REEF_EXPRESS_DEFAULT_TAG');
            if (tagAttribute) {
                this.commonService.currentProject.tlsDefaultTag = tagAttribute.value;
            }
            const isTlsExpress = attributes.find(a => a.name === 'SELF_SERVICE');
            if (isTlsExpress?.value) {
                this.commonService.currentProject.isTlsExpress = true;
            }
        }
    }

    loadProjectSettings(): void {
        this.restService.post(Endpoint.projectManager_getSearchSettings, { handle: this.commonService.currentProject.handle }).then(response => {
            if (response && response.searchSettings) {
                this.commonService.searchDialect = SearchDialect[response.searchSettings.searchDialect] as SearchDialect;
            }
        });

        this.restService.post(Endpoint.realmManager_getMailServerConfig).then(response => {
            if (response && response.mailServerConfig) {
                this.commonService.mailServerConfig = response.mailServerConfig;
            }
        });
        this.commonService.isGetPreferencesResponse = false;
        this.restService.post(Endpoint.projectManager_getPreference, { ownerId: this.commonService.currentProject.handle }).then(response => {
            if (response && response.preferences) {
                this.commonService.dashboardReportsEnabled = response.preferences.dashboardReportsEnabled;
                this.commonService.isGetPreferencesResponse = true;
                if (this.commonService.hideArchivedItems !== response.preferences.suppressArchivedNavTree) {
                    this.commonService.hideArchivedItems = response.preferences.suppressArchivedNavTree;
                    this.commonService.sendMessage(MessageType.refreshLeftTreeNodes, { archivedFlagChanged: true });
                }
                this.commonService.lastAccessTimeForArchival = response.preferences.lastAccessTimeForArchival;
                this.commonService.suppressDuplicates = response.preferences.suppressDuplicates;
            }
        });

        this.restService.post(Endpoint.projectManager_getProjectConfig, { projectId: this.commonService.currentProject.handle }).then(response => {
            if (response) {
                this.commonService.currentProject.deDupScope = response.deDuplicateScope;
                if (response.parsingConfiguration) {
                    this.commonService.tokenizerConfigVersion = response.parsingConfiguration.tokenizerConfig.scheme === 1 ? 'V1' : 'V2';
                    this.commonService.externalParsingLibrary = response.externalParsingLibrary;
                }
            }
        });

        // TODO - works on review enhancements branch - 120 server
        /* this.restService.post(Endpoint.keyboardShortcutManager_isKeyboardShortcutsEnabled, { handle: this.commonService.currentProject.handle }).then(response => {
            if (response) {
                this.commonService.useShortcutsApplication = true;
            } else {
                this.commonService.useShortcutsApplication = false;
            }
        }, error => {
        }); */
    }

    loadData(refresh = false): void {
        this.isRefresh = refresh;
        if (this.isSuperSystemUser) {
            this.gridName = 'organizations & projects';
            this.requestInfo.endpoint = Endpoint.realmManager_listSystemUserProjectsByUserName;
            this.requestInfo.responseName = 'userOrgProjects';
            this.requestInfo.data = { startIndex: 0, count: 0, userName: this.commonService.currentUser.userName, filters: this.getFilters() };
        } else {
            this.gridName = 'projects';
            this.requestInfo.endpoint = Endpoint.orgManager_listUserProjectsForAllOrgs;
            this.requestInfo.responseName = 'userOrgProjects';
            this.requestInfo.data = { contextHandle: '', startIndex: 0, count: 0, filters: this.getFilters(), systemUser: this.commonService.currentUser?.importedFromSystemUser };
        }
        this.genericGrid.loadObservableData();
    }

    mapOrganizations(organizations: Array<any>): Array<ProjectListItem> {
        return organizations.map(org => {
            const organization = ProjectListItem.fromOrganization(org);
            this.mapProjects(org.projects, organization);
            return organization;
        });
    }

    mapProjects(projects: Array<any>, organization: ProjectListItem): Array<ProjectListItem> {
        if (!projects) {
            projects = [];
        }
        return projects.map(prj => ProjectListItem.fromProject(prj, organization));
    }

    filterData(str: string): void {
        this.debounceFilter(str);
    }

    filterAfterBounce(str: string): void {
        this.activeFilter = str;
        this.loadData();
    }


    getFilters(): any[] {
        const filters = [];
        if (this.activeFilter && this.activeFilter.length > 0) {
            filters.push({ attribute: 'ORG', operator: 'LIKE', value: this.activeFilter });
            filters.push({ attribute: 'PROJECT', operator: 'LIKE', value: this.activeFilter });
            filters.push({ attribute: 'DESCRIPTION', operator: 'LIKE', value: this.activeFilter });
            filters.push({ attribute: 'ORG_DESCRIPTION', operator: 'LIKE', value: this.activeFilter });
            filters.push({ attribute: 'CREATED_BY', operator: 'LIKE', value: this.activeFilter });
        }
        return filters;
    }

    selectOrganization(organization: ProjectListItem, isContextMenuOption: boolean): Promise<any> {
        this.commonService.currentUser.roleHandles[0] = organization.role ? organization.role.handle : organization.parent.role.handle;
        this.commonService.currentUser.customerName = organization.organizationName ? organization.organizationName : organization.parent.organizationName;
        this.commonService.contextHandle = this.commonService.currentUser.customerName;
        return new Promise<any>((resolve) => {
            this.restService.post(Endpoint.realmManager_initializeOrganization, { organizationName: this.commonService.currentUser.customerName })
                .then(response => {
                    resolve(true);
                    if (!isContextMenuOption) {
                        this.getUserPermissions();
                    }
                });
        });
    }

    selectProject(item: ProjectListItem): void {
        this.commonService.currentProject = item.data;
        this.commonService.projectName = this.commonService.currentProject.name;
        this.commonService.matrixGridsSelection = [];
        if (item.parent) {
            this.commonService.currentUser.roleHandles[0] = item.parent.role.handle;
            this.commonService.currentUser.customerName = item.parent.organizationName;
            this.selectOrganization(item.parent, false);
        }
        this.commonService.contextHandle = this.commonService.currentProject.handle;
    }

    loadDataFinishedCallback = () => {
        if (this.commonService.appContext?.prjHandle) {
            // auto select project coming from Job History
            const prjToSelect = this.genericGrid.getData().find(prj => prj.handle === this.commonService.appContext.prjHandle);
            if (prjToSelect) {
                this.selectProject(prjToSelect);
                this.commonService.appContext.prjHandle = null;
            }
        }
    }

    errorOnLoadingDataCallback = () => {
        this.isRefresh = false;
    }

    projectListContextMenuActions(actionType: string, item: ProjectListItem): void {
        switch (actionType) {
            case 'newProject': {
                this.selectOrganization(item, true).then(() => {
                    this.setSystemUserPermissions(item);
                    this.commonService.openModal(NewProjectModalComponent, { modalData: { isExpressSystem: this.isExpressSystem }, class: 'vertical-percent-100 horizontal-percent-100' }).then(response => {
                        if (response && response.modalResult && response.modalData) {
                            let newProject;
                            if (this.isSuperSystemUser) {
                                newProject = response.modalData;
                            } else {
                                newProject = new Project(response.modalData);
                            }
                            this.selectProject(ProjectListItem.fromProject(newProject, item));
                        }
                    });
                });
                break;
            }
            case 'importProject': {
                this.selectOrganization(item, true).then(() => {
                    this.setSystemUserPermissions(item);
                    this.commonService.openModal(ImportProjectModalComponent, { class: 'vertical-percent-80 horizontal-px-800' });
                });
                break;
            }
            case 'openSettings': {
                if (item.isParent) {
                    this.selectOrganization(item, true).then(() => {
                        this.setSystemUserPermissions(item);
                        Object.assign(item, { openType: SettingsType.organization });
                        this.commonService.openModal(SettingsModalComponent, { modalData: item, class: 'vertical-percent-100 horizontal-percent-100', keyboard: false });
                    });
                } else {
                    this.selectOrganization(item.parent, true).then(() => {
                        this.isBusyMessage = 'Loading project permissions ...';
                        this.isBusy = true;
                        this.restService.post(Endpoint.permissionManager_getCombinedUserRole, { entityHandle: item.data.handle }).then(roleResponse => {
                            this.isBusy = false;
                            if (roleResponse && roleResponse.role && roleResponse.role.objectActionList) {
                                const objectActionList = (this.isSuperSystemUser || this.commonService.currentUser.importedFromSystemUser) ?
                                    [...this.systemRolePermissions, ...roleResponse.role.objectActionList] : roleResponse.role.objectActionList;
                                Permissions.setPermissions(objectActionList);
                            }
                            this.setSystemUserPermissions(item.parent);
                            Object.assign(item, { openType: SettingsType.project });
                            this.commonService.openModal(SettingsModalComponent, { modalData: item, class: 'vertical-percent-100 horizontal-percent-100', keyboard: false });
                        });
                    });
                }
                break;
            }
            case 'exportProject': {
                this.selectOrganization(item, true).then(() => {
                    this.commonService.openModal(ExportProjectModalComponent, { modalData: item.data, class: 'vertical-percent-80 horizontal-px-800' });
                });
                break;
            }
            case 'editProject': {
                this.selectOrganization(item, true).then(() => {
                    this.commonService.openModal(CreateEditEntityModalComponent, { modalData: { data: item.data, isExpressSystem: this.isExpressSystem } });
                });
                break;
            }
            case 'deleteProject': {
                this.selectOrganization(item, true).then(() => {
                    this.commonService.openModal(DeleteEntityModalComponent, { modalData: item.data });
                });
                break;
            }
        }
    }

    setSystemUserPermissions(item: ProjectListItem): void {
        if (this.isSuperSystemUser) {
            Permissions.setPermissions([...this.systemRolePermissions, ...item.role.objectActionList]);
        }
    }

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
    }
}
